import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import axios from 'axios';
import { useWorkspace } from '../../context/WorkspaceContext';
import {
  Drawer,
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  CircularProgress,
  Stack,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

/**
 * History drawer: Recent (auto-saved) vs Saved (pinned). Load / delete / promote.
 * @param {string} apiEndpoint e.g. '/api/beam-formulae/saved-analyses'
 * @param {(data: { input_data, results_data, session_id, raw }) => void} onLoad
 * @param {(isAutoSaved: boolean) => object} getSavePayload returns body fields for save_from_analysis (without name/description when dialog collects them)
 */
const CalculationHistory = forwardRef(function CalculationHistory(
  {
    apiEndpoint,
    onLoad,
    getSavePayload,
    appLabel = 'Calculation',
    renderSummary,
    iconButtonProps = {},
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [recent, setRecent] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteItem, setPromoteItem] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const { isLoggedIn, openLogin } = useWorkspace();

  const base = (apiEndpoint || '').replace(/\/$/, '');

  const fetchLists = useCallback(async () => {
    if (!base) return;
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${base}/recent/`),
        axios.get(`${base}/saved/`),
      ]);
      setRecent(Array.isArray(r.data) ? r.data : []);
      setSaved(Array.isArray(s.data) ? s.data : []);
    } catch (e) {
      console.error('CalculationHistory fetch', e);
      setRecent([]);
      setSaved([]);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    if (open) fetchLists();
  }, [open, fetchLists]);

  const saveAuto = useCallback(
    async (extra = {}) => {
      if (!base || !getSavePayload) return;
      if (!isLoggedIn) return;
      try {
        const payload = { ...getSavePayload(true), ...extra, is_auto_saved: true };
        await axios.post(`${base}/save_from_analysis/`, payload);
      } catch (e) {
        console.error('Auto-save failed', e);
      }
    },
    [base, getSavePayload, isLoggedIn],
  );

  useImperativeHandle(ref, () => ({ saveAuto }), [saveAuto]);

  const handleLoad = async (item) => {
    const sid = item.session_id;
    if (!sid || !base) return;
    try {
      const res = await axios.get(`${base}/${sid}/`);
      const d = res.data;
      onLoad?.({
        input_data: d.input_data,
        results_data: d.results_data,
        session_id: sid,
        raw: d,
      });
      setOpen(false);
    } catch (e) {
      console.error('Load failed', e);
    }
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    const sid = item.session_id;
    if (!sid || !base) return;
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${base}/${sid}/`);
      fetchLists();
    } catch (err) {
      console.error(err);
    }
  };

  const openPromote = (item) => {
    setPromoteItem(item);
    setSaveName(item.name || '');
    setPromoteOpen(true);
  };

  const handlePromote = async () => {
    if (!promoteItem || !base) return;
    const sid = promoteItem.session_id;
    try {
      await axios.patch(`${base}/${sid}/promote/`, {
        name: saveName || promoteItem.name,
        description: saveDesc,
      });
      setPromoteOpen(false);
      setPromoteItem(null);
      setSaveDesc('');
      fetchLists();
    } catch (e) {
      console.error(e);
    }
  };

  const submitManualSave = async () => {
    if (!getSavePayload || !base) return;
    if (!isLoggedIn) {
      openLogin();
      return;
    }
    const payload = getSavePayload(false);
    try {
      await axios.post(`${base}/save_from_analysis/`, {
        ...payload,
        name: saveName || payload.name,
        description: saveDesc || payload.description,
        is_auto_saved: false,
      });
      setSaveDialogOpen(false);
      setSaveName('');
      setSaveDesc('');
      fetchLists();
    } catch (e) {
      console.error(e);
    }
  };

  const list = tab === 0 ? recent : saved;

  return (
    <>
      <IconButton
        color="inherit"
        onClick={() => setOpen(true)}
        title="History"
        aria-label="Calculation history"
        {...iconButtonProps}
      >
        <HistoryIcon />
      </IconButton>
      {getSavePayload && (
        <IconButton
          color="inherit"
          onClick={() => {
            if (!isLoggedIn) {
              openLogin();
              return;
            }
            setSaveDialogOpen(true);
            setSaveName('');
            setSaveDesc('');
          }}
          title="Save"
          aria-label="Save calculation"
          {...iconButtonProps}
        >
          <SaveIcon />
        </IconButton>
      )}

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 400, p: 2 }} role="presentation">
          <Typography variant="h6" gutterBottom>
            {appLabel} — history
          </Typography>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Recent" />
            <Tab label="Saved" />
          </Tabs>
          {!isLoggedIn ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Sign in with a username to save and load your calculations.
              <Button size="small" variant="outlined" onClick={openLogin} sx={{ display: 'block', mt: 2 }}>
                Sign in
              </Button>
            </Typography>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : list.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No entries yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {list.map((item) => (
                <Box
                  key={item.session_id}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.name || 'Untitled'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.created_at
                      ? new Date(item.created_at).toLocaleString()
                      : ''}
                  </Typography>
                  {renderSummary ? (
                    <Box sx={{ mt: 0.5 }}>{renderSummary(item)}</Box>
                  ) : null}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                    <Button
                      size="small"
                      startIcon={<FolderOpenIcon />}
                      onClick={() => handleLoad(item)}
                    >
                      Load
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDelete(e, item)}
                      aria-label="Delete"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    {tab === 0 && (
                      <Button
                        size="small"
                        startIcon={<PushPinIcon />}
                        onClick={() => openPromote(item)}
                      >
                        Save
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Save calculation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            minRows={2}
            value={saveDesc}
            onChange={(e) => setSaveDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitManualSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={promoteOpen} onClose={() => setPromoteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Save to pinned list</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePromote}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default CalculationHistory;
